import without from "lodash/without";
import { KPIsReviewSidepanelData } from "PFApp/components/kpis_review_sidepanel/kpis_review_sidepanel.types";
import { TemplateKey } from "PFApp/constants/templates";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { ActionIcon } from "PFComponents/action_icon";
import { Flex } from "PFComponents/containers";
import { Id, WorkflowActivity } from "PFTypes";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ActivityActions } from "../../activity_actions";
import css from "./rows_actions.module.scss";

type RowActionsCellProps = {
  item: WorkflowActivity["activity"];
  pinnedIds?: number[];
  type: TemplateKey.Role | TemplateKey.Engagement | TemplateKey.AuditRole;
  setPinnedIds?: React.Dispatch<React.SetStateAction<number[]>>;
  onMarkAsRead?: (activityId: Id) => Promise<void>;
  onShare?: (activityId: Id) => void;
  onRefetch?: VoidFunction;
  onKPIsReviewClick?: (data: KPIsReviewSidepanelData) => void;
};

export const RowActionsCell = ({
  item,
  type,
  pinnedIds,
  onRefetch,
  onShare,
  onMarkAsRead,
  setPinnedIds,
  onKPIsReviewClick
}: RowActionsCellProps) => {
  const { t } = useTranslation("workflow");
  const { showGrowl } = useGrowlStoreActions();

  const isPinned = useMemo(() => item.id && (pinnedIds?.includes(item.id) ?? false), [item.id, pinnedIds]);

  const unpinActivity = useCallback(
    () => setPinnedIds?.(without(pinnedIds, Number(item.id))),
    [item.id, setPinnedIds, pinnedIds]
  );

  const handleMarkAsRead = () => {
    onMarkAsRead?.(item.id).then(() => {
      showGrowl({
        message: t("parts.markedAsRead")
      });
    });
  };

  return (
    <Flex className={css.rowActions} justifyContent="end" gap="spacingLg">
      {isPinned && (
        <ActionIcon
          data-qa-id={`WorkflowPageUnpinActivity.${item.id}`}
          title={t("parts.threeDotsMenu.unmarkImportant")}
          name="pin"
          onClick={unpinActivity}
        />
      )}
      {onMarkAsRead && <ActionIcon name="unseen" onClick={handleMarkAsRead} />}
      <ActivityActions
        item={item}
        type={type}
        pinnedIds={pinnedIds}
        setPinnedIds={setPinnedIds}
        onRefetch={onRefetch}
        onShare={onShare}
        onKPIsReviewClick={onKPIsReviewClick}
      />
    </Flex>
  );
};
